// The order dialog (spec/13 §5, F-POS-15): opened from a row, centred over the list, it shows one sale in full —
// its total and state first, then its lines, discounts, tenders, refunds and receipt, and the actions
// the member may take (take payment, apply a code, cancel, refund, approve a refund, print and resend
// the receipt). Money is in tabular figures (`.console-money`); an action re-reads the sale, so the
// dialog stays live. Only permitted actions are offered, and the server checks each again.

import type { OrganizationPlayer, Sale, SaleDiscount, SaleDocumentDelivery, SaleLine, SalePayment, SaleRefund } from '@clavian/api-client';
import { formatOrderNumber } from '@clavian/domain/menu-choice';
import { receiptTenderRows, type ReceiptTenderRow } from '@clavian/domain/receipt-tenders';
import { useTranslation } from '@clavian/i18n/react';
import { Avatar, Badge, Button, Dialog, RadioGroup, SelectField, Skeleton, TextField, useToast } from '@clavian/ui-web';
import { useState, type FormEvent, type ReactNode } from 'react';

import { PROBLEM, problemCode, problemDetail } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { Icon } from '../../shell/nav-icons';
import { Facts, Notice, SearchField, Section } from '../../ui';
import { cashSuggestions, changeDue, minorToMajor, moneyFrom } from '../money';
import { pickerResults, PLAYER_SEARCH_MIN, playerName, usePlayerProfile, usePlayerSearch } from '../players/queries';
import { PrintOutcomeNote, usePrintJobHandler, type PrintOutcome } from '../printing/outcome';
import { formatMoney } from '../sessions/money';
import { useWalletBalance } from '../wallet/queries';
import { isAwaitingPayment, ORDER_STATUS_TONE, OrderSource, orderStatusKey, orderTone, useStationLabels } from './source';

import '../sessions/sessions.css';
import './orders.css';
import {
  useApplyDiscountCode,
  useApproveRefund,
  useCancelSale,
  usePrintSaleReceipt,
  useRefundSale,
  useSale,
  useOpenSaleDocument,
  useSaleDocument,
  useSendSaleDocument,
  useTakeSalePayment,
} from './queries';

const ORDERS_READ = 'orders:read';
const ORDERS_CREATE = 'orders:create';
const ORDERS_VOID = 'orders:void';
const ORDERS_REFUND = 'orders:refund';
const ORDERS_REFUND_APPROVE = 'orders:refund_approve';
const PLAYERS_READ = 'players:read';

/** The venue's own tenders (spec/06 §9.1): cash, its card machine (F-PAY-02) and its UPI QR. */
const TENDERS = ['cash', 'counter_card', 'counter_upi'] as const;
type Tender = (typeof TENDERS)[number];

export interface OrderDialogProps {
  readonly organizationId: string;
  readonly venueId: string;
  readonly saleId: string | null;
  readonly can: (permission: string) => boolean;
  readonly onClose: () => void;
}

export function OrderDialog({ organizationId, venueId, saleId, can, onClose }: OrderDialogProps): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const query = useSale(organizationId, venueId, saleId ?? '', saleId !== null);

  let body: ReactNode;
  if (saleId === null) {
    body = null;
  } else if (query.data === undefined) {
    body = query.isError ? (
      <Notice tone="danger" role="alert">
        {problem.text(query.error, { fallback: t('orders.drawer.unavailable') })}
      </Notice>
    ) : (
      <div className="console-session" role="status" aria-busy="true" aria-label={t('orders.drawer.loading')}>
        <Skeleton height="7rem" />
        <Skeleton shape="text" lines={4} />
      </div>
    );
  } else {
    body = <OrderBody organizationId={organizationId} venueId={venueId} sale={query.data} can={can} />;
  }
  // The object as the eyebrow (the dialog pattern): the order's number for the day — what the counter calls out — once
  // it is read, so the header never says "Order" over "Order details".
  const reference = query.data === undefined ? t('orders.drawer.eyebrow') : (formatOrderNumber(query.data.orderNumber) ?? t('orders.drawer.eyebrow'));

  return (
    <Dialog
      open={saleId !== null}
      onOpenChange={(open) => (open ? undefined : onClose())}
      size="lg"
      eyebrow={reference}
      title={saleId === null ? '' : t('orders.drawer.title')}
    >
      {body}
    </Dialog>
  );
}

function OrderBody({ organizationId, venueId, sale, can }: { readonly organizationId: string; readonly venueId: string; readonly sale: Sale; readonly can: (permission: string) => boolean }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const document = useSaleDocument(organizationId, venueId, sale.id, true, sale.status === 'paid');
  // The printable 80 mm receipt or A4 tax invoice (save as PDF from the print dialog), from the server (F-CMP-07).
  const printable = useOpenSaleDocument(organizationId, venueId, sale.id);
  const stationLabel = useStationLabels(organizationId, venueId);
  const status = orderStatusKey(sale);
  const tone = orderTone(sale);

  return (
    <div className="console-session">
      {/* The order's head is a card with its state's rail (ADR-0043): warn while money is owed, quiet once it is over. */}
      <div className="console-order__hero console-rail" data-rail={tone === undefined ? (status === 'paid' ? 'ok' : undefined) : tone}>
        <div className="console-order__hero-text">
          <Badge tone={ORDER_STATUS_TONE[status]} dot>
            {t(`orders.status.${status}`)}
          </Badge>
          <span className="console-order__total">{formatMoney(sale.total)}</span>
          <OrderSource organizationId={organizationId} sale={sale} stationLabel={stationLabel} canReadPlayers={can(PLAYERS_READ)} />
          <span className="console-subtle">
            {sale.orderNumber === null ? null : <span className="console-order__number console-num">{formatOrderNumber(sale.orderNumber)} · </span>}
            <span className="console-code">{sale.id}</span>
          </span>
        </div>
        {sale.outstanding.amount > 0 ? (
          <div className="console-order__owed">
            <span className="console-eyebrow">{t('orders.field.outstanding')}</span>
            <span className="console-money">{formatMoney(sale.outstanding)}</span>
          </div>
        ) : null}
      </div>

      {isAwaitingPayment(sale) ? (
        <Notice tone="info">{t('orders.kiosk.awaiting')}</Notice>
      ) : null}

      <Actions organizationId={organizationId} venueId={venueId} sale={sale} hasDocument={document.data != null} can={can} />

      <Lines lines={sale.lines} discounts={sale.discounts} />

      <Facts
        columns={3}
        items={[
          { key: 'subtotal', label: t('orders.field.subtotal'), value: <span className="console-money">{formatMoney(sale.subtotal)}</span> },
          ...(sale.discount.amount > 0 ? [{ key: 'discount', label: t('orders.field.discount'), value: <span className="console-money">−{formatMoney(sale.discount)}</span> }] : []),
          ...(sale.tax.amount > 0 ? [{ key: 'tax', label: t('orders.field.tax'), value: <span className="console-money">{formatMoney(sale.tax)}</span> }] : []),
          { key: 'paid', label: t('orders.field.paid'), value: <span className="console-money">{formatMoney(sale.paid)}</span> },
          ...(sale.refunded.amount > 0 ? [{ key: 'refunded', label: t('orders.field.refunded'), value: <span className="console-money">−{formatMoney(sale.refunded)}</span> }] : []),
        ]}
      />

      {sale.payments.length > 0 ? (
        <Section title={t('orders.payments.title')} titleAs="h3" body="rows">
          <PaymentRows organizationId={organizationId} sale={sale} canReadPlayers={can(PLAYERS_READ)} />
        </Section>
      ) : null}

      {sale.refunds.length > 0 ? <Refunds organizationId={organizationId} venueId={venueId} saleId={sale.id} refunds={sale.refunds} can={can} /> : null}

      {document.data != null ? (
        <Section
          title={t('orders.document.title')}
          titleAs="h3"
          actions={
            <span className="console-order__print">
              <Button variant="secondary" size="sm" busy={printable.isPending && printable.variables === 'a4'} onClick={() => printable.mutate('a4')}>
                {document.data.kind === 'tax_invoice' ? t('orders.document.printInvoice') : t('orders.document.printA4')}
              </Button>
            </span>
          }
        >
          <p>
            <span className="console-muted">{document.data.title}</span> <span className="console-code">{document.data.formattedNumber}</span>
          </p>
          {document.data.buyer.gstin !== null ? (
            <p className="console-muted">
              {t('orders.document.buyer', { name: document.data.buyer.name ?? '', gstin: document.data.buyer.gstin })}
              {document.data.buyer.placeOfSupply === 'inter_state' ? ` · ${t('orders.document.igst')}` : ''}
            </p>
          ) : null}
          {document.data.kind === 'tax_invoice' && document.data.totals.taxPaidAtTopUp !== undefined && document.data.totals.taxPaidAtTopUp.amount > 0 ? (
            <p className="console-muted">{t('orders.document.taxPaidAtTopUp', { amount: formatMoney(document.data.totals.taxPaidAtTopUp) })}</p>
          ) : null}
          {printable.isError ? (
            <Notice tone="danger" role="alert">
              {problem.text(printable.error, { fallback: t('orders.document.printFailed') })}
            </Notice>
          ) : null}
          {document.data.delivery != null ? <DeliveryLine delivery={document.data.delivery} /> : null}
          {document.data.creditNotes.length > 0 ? (
            <ul className="console-session__plan" aria-label={t('orders.document.creditNotes')}>
              {document.data.creditNotes.map((note) => (
                <li key={note.id} className="console-session__plan-row">
                  <span>
                    {t('orders.document.creditNote')} <span className="console-code">{note.formattedNumber}</span>
                  </span>
                  <span className="console-money">−{formatMoney(note.total)}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </Section>
      ) : null}
    </div>
  );
}

function Lines({ lines, discounts }: { readonly lines: readonly SaleLine[]; readonly discounts: readonly SaleDiscount[] }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Section title={t('orders.lines.title')} titleAs="h3" body="rows">
      <ul className="console-session__plan">
        {lines.map((line: SaleLine) => (
          <li key={line.id} className="console-session__plan-row">
            <span>
              <span className="console-num console-subtle">{line.quantity}×</span> {line.name}
            </span>
            <span className="console-money">{formatMoney(line.total)}</span>
          </li>
        ))}
        {discounts.map((discount: SaleDiscount, index) => (
          <li key={`discount-${index}`} className="console-session__plan-row">
            <span>{discount.kind === 'code' ? t('orders.discount.code', { code: discount.code ?? '' }) : t('orders.discount.manual')}</span>
            <span className="console-money">−{formatMoney(discount.amount)}</span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function Refunds({ organizationId, venueId, saleId, refunds, can }: { readonly organizationId: string; readonly venueId: string; readonly saleId: string; readonly refunds: readonly SaleRefund[]; readonly can: (permission: string) => boolean }): ReactNode {
  const { t } = useTranslation('console');
  const approve = useApproveRefund(organizationId, venueId, saleId);
  const canApprove = can(ORDERS_REFUND_APPROVE);

  return (
    <Section title={t('orders.refunds.title')} titleAs="h3" body="rows">
      <ul className="console-session__plan">
        {refunds.map((refund: SaleRefund) => (
          <li key={refund.id} className="console-session__plan-row">
            <span>
              {t(`orders.refundStatus.${refund.status}`)} · {refund.tender === 'online' ? t('orders.refund.tenderOnline') : t(`orders.tender.${refund.tender}`, { defaultValue: refund.tender })}
            </span>
            <span className="console-order__refund-right">
              <span className="console-money">−{formatMoney(refund.amount)}</span>
              {refund.status === 'pending_approval' && canApprove ? (
                <Button variant="secondary" size="sm" busy={approve.isPending} onClick={() => approve.mutate(refund.id)}>
                  {t('orders.refunds.approve')}
                </Button>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

type Action = 'payment' | 'code' | 'refund' | 'send' | 'cancel' | null;

function Actions({ organizationId, venueId, sale, hasDocument, can }: { readonly organizationId: string; readonly venueId: string; readonly sale: Sale; readonly hasDocument: boolean; readonly can: (permission: string) => boolean }): ReactNode {
  const { t } = useTranslation('console');
  const [open, setOpen] = useState<Action>(null);
  const receipt = usePrintSaleReceipt(organizationId, venueId, sale.id);
  const handleJob = usePrintJobHandler();
  const [printed, setPrinted] = useState<PrintOutcome | null>(null);

  const isOpen = sale.status === 'placed';
  const owes = sale.outstanding.amount > 0 && (sale.status === 'placed' || sale.status === 'partially_paid');
  const isPaid = sale.status === 'paid' || sale.status === 'partially_refunded';

  const canPay = can(ORDERS_CREATE) && owes;
  const canCode = can(ORDERS_CREATE) && isOpen;
  const canCancel = can(ORDERS_VOID) && isOpen && sale.paid.amount === 0;
  const canRefund = can(ORDERS_REFUND) && isPaid;
  const canSend = hasDocument;
  // A receipt for money taken (F-OPS-08): to the receipt printer, or the browser when the venue has none.
  const canPrint = can(ORDERS_READ) && (isPaid || sale.status === 'refunded');
  if (!canPay && !canCode && !canCancel && !canRefund && !canSend && !canPrint) return null;

  function printReceipt(): void {
    setPrinted(null);
    receipt.mutate(undefined, {
      onSuccess: (result) => setPrinted(handleJob(result.job, result.target, t('orders.receipt.windowTitle'), t('printing.receiptPrinter'))),
      onError: () => setPrinted({ kind: 'failed' }),
    });
  }

  const toggle = (action: Action) => setOpen(open === action ? null : action);

  return (
    <div className="console-session__actions-block">
      <div className="console-session__actions" role="group" aria-label={t('orders.actions.title')}>
        {canPay ? <Button variant="primary" size="sm" onClick={() => toggle('payment')}>{t('orders.actions.takePayment')}</Button> : null}
        {canCode ? <Button variant="secondary" size="sm" onClick={() => toggle('code')}>{t('orders.actions.applyCode')}</Button> : null}
        {canRefund ? <Button variant="secondary" size="sm" onClick={() => toggle('refund')}>{t('orders.actions.refund')}</Button> : null}
        {canPrint ? <Button variant="secondary" size="sm" icon={<Icon name="print" />} busy={receipt.isPending} onClick={printReceipt}>{t('orders.actions.printReceipt')}</Button> : null}
        {canSend ? <Button variant="secondary" size="sm" onClick={() => toggle('send')}>{t('orders.actions.resend')}</Button> : null}
        {canCancel ? <Button variant="danger" size="sm" onClick={() => toggle('cancel')}>{t('orders.actions.cancel')}</Button> : null}
      </div>
      <PrintOutcomeNote outcome={printed} />
      {open === 'payment' ? <PaymentForm organizationId={organizationId} venueId={venueId} sale={sale} onDone={() => setOpen(null)} /> : null}
      {open === 'code' ? <CodeForm organizationId={organizationId} venueId={venueId} saleId={sale.id} onDone={() => setOpen(null)} /> : null}
      {open === 'refund' ? <RefundForm organizationId={organizationId} venueId={venueId} sale={sale} canPickPlayer={can(PLAYERS_READ)} onDone={() => setOpen(null)} /> : null}
      {open === 'send' ? <SendForm organizationId={organizationId} venueId={venueId} saleId={sale.id} onDone={() => setOpen(null)} /> : null}
      {open === 'cancel' ? <CancelForm organizationId={organizationId} venueId={venueId} saleId={sale.id} onDone={() => setOpen(null)} /> : null}
    </div>
  );
}

/** Minor units from a decimal amount typed in the major unit, using the currency's own scale. */
function toMinor(value: string, currency: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '' || !/^\d+(\.\d+)?$/.test(trimmed)) return null;
  const digits = new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2;
  return Math.round(Number(trimmed) * 10 ** digits);
}

function PaymentForm({ organizationId, venueId, sale, onDone }: { readonly organizationId: string; readonly venueId: string; readonly sale: Sale; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const problem = useProblem();
  const take = useTakeSalePayment(organizationId, venueId, sale.id);
  const currency = sale.outstanding.currency;
  const [tender, setTender] = useState<Tender>('cash');
  const [amount, setAmount] = useState(minorToMajor(sale.outstanding.amount, currency));
  const [tendered, setTendered] = useState('');
  // Where a cash overpayment's change goes: back in cash (the default, unchanged behaviour) or into the
  // attached player's wallet (F-POS-12, spec/06 §9). Only offered when the venue accepts it and there is change.
  const [changeToWallet, setChangeToWallet] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  // What is applied to the sale (the server caps it at the outstanding); for cash the player can hand over
  // more, so the tendered figure is kept apart and the server records `change = tendered − amount` (F-POS-12,
  // the same as the counter Sell). Non-cash tenders have no change.
  const applyMinor = toMinor(amount, currency);
  const tenderedMoney = tender === 'cash' && tendered.trim() !== '' ? moneyFrom(tendered, currency) : null;
  const change = tenderedMoney !== null && applyMinor !== null ? changeDue(tenderedMoney.amount, applyMinor) : null;
  // The venue accepts change into the wallet (wallet on + redemption, from the sale read), the sale has a player,
  // and there is change to send. Only then is the destination choice shown; the server re-checks every one.
  const offerWalletChange = tender === 'cash' && sale.changeToWalletAvailable === true && sale.playerId !== null && change !== null && change > 0;
  const toWallet = offerWalletChange && changeToWallet;

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const minor = toMinor(amount, currency);
    if (minor === null || minor <= 0) {
      setError(t('orders.payment.badAmount'));
      return;
    }
    const handedOver = tender === 'cash' && tendered.trim() !== '' ? moneyFrom(tendered, currency) : null;
    if (tender === 'cash' && tendered.trim() !== '' && handedOver === null) {
      setError(t('orders.payment.badAmount'));
      return;
    }
    setError(undefined);
    take.mutate(
      { tender, amount: { amount: minor, currency }, ...(handedOver === null ? {} : { tendered: handedOver }), ...(toWallet ? { changeToWallet: true } : {}) },
      {
        onSuccess: () => {
          show({ tone: 'ok', title: t('orders.payment.done') });
          onDone();
        },
        onError: (mutationError) => {
          setError(problemCode(mutationError) === PROBLEM.conflict ? t('orders.payment.conflict') : problem.text(mutationError, { fallback: t('orders.payment.refused') }));
        },
      },
    );
  }

  return (
    <form className="console-session__form" aria-label={t('orders.actions.takePayment')} noValidate onSubmit={submit}>
      <SelectField
        label={t('orders.payment.tender')}
        options={TENDERS.map((value) => ({ value, label: t(`orders.tender.${value}`) }))}
        value={tender}
        onValueChange={(value) => {
          setTender(value as Tender);
          setTendered('');
          setChangeToWallet(false);
          setError(undefined);
        }}
      />
      <TextField label={t('orders.payment.amount')} required inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} error={tender === 'cash' ? undefined : error} />
      {tender === 'cash' ? (
        <>
          <TextField
            label={t('orders.payment.tendered', { currency })}
            showOptional
            inputMode="decimal"
            value={tendered}
            onChange={(e) => setTendered(e.target.value)}
            description={t('orders.payment.tenderedHint')}
            error={error}
          />
          {/* One tap for what a player usually hands over: the amount exactly, then the next round notes. */}
          {applyMinor !== null && applyMinor > 0 ? (
            <div className="console-quick-tender" role="group" aria-label={t('orders.payment.quickLabel')}>
              {[applyMinor, ...cashSuggestions(applyMinor, currency)].map((value, index) => (
                <Button
                  key={value}
                  type="button"
                  size="sm"
                  variant="secondary"
                  aria-pressed={tenderedMoney?.amount === value}
                  onClick={() => setTendered(minorToMajor(value, currency))}
                >
                  {index === 0 ? t('orders.payment.exact') : formatMoney({ amount: value, currency })}
                </Button>
              ))}
            </div>
          ) : null}
          {/* A cash overpayment's change: hand it back (the default), or add it to the player's wallet where the
              venue accepts it (F-POS-12, spec/06 §9). The choice shows only with change and the capability. */}
          {offerWalletChange ? (
            <RadioGroup
              label={t('orders.payment.changeLabel')}
              orientation="horizontal"
              options={[
                { value: 'cash', label: t('orders.payment.giveBack') },
                { value: 'wallet', label: t('orders.payment.toWallet') },
              ]}
              value={toWallet ? 'wallet' : 'cash'}
              onValueChange={(value) => {
                setChangeToWallet(value === 'wallet');
                setError(undefined);
              }}
            />
          ) : null}
          {change !== null ? (
            <p className="console-change-preview" role="status">
              <span>{toWallet ? t('orders.payment.addedToWallet') : t('orders.payment.giveBack')}</span>
              <strong className="clav-figure">{formatMoney({ amount: change, currency })}</strong>
            </p>
          ) : null}
        </>
      ) : null}
      <FormActions busy={take.isPending} submitLabel={t('orders.payment.submit')} onCancel={onDone} />
    </form>
  );
}

function CodeForm({ organizationId, venueId, saleId, onDone }: { readonly organizationId: string; readonly venueId: string; readonly saleId: string; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const apply = useApplyDiscountCode(organizationId, venueId, saleId);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (code.trim() === '') {
      setError(t('orders.code.required'));
      return;
    }
    setError(undefined);
    apply.mutate(code.trim(), { onSuccess: onDone, onError: (failure) => setError(problem.text(failure, { fallback: t('orders.code.refused') })) });
  }

  return (
    <form className="console-session__form" aria-label={t('orders.actions.applyCode')} noValidate onSubmit={submit}>
      <TextField label={t('orders.code.label')} required value={code} onChange={(e) => setCode(e.target.value)} error={error} />
      <FormActions busy={apply.isPending} submitLabel={t('orders.code.submit')} onCancel={onDone} />
    </form>
  );
}

/**
 * The order's tenders as its receipt shows them (ADR-0049, `receiptTenderRows`): a ₹120 order paid with a ₹200 note
 * reads "Cash received ₹200.00" and "Change ₹80.00", or "Change added to Asha's wallet ₹80.00"; a split lists each
 * tender's amount with the cash received and change under the cash one. A payment with no hand-over shows its amount.
 */
function PaymentRows({ organizationId, sale, canReadPlayers }: { readonly organizationId: string; readonly sale: Sale; readonly canReadPlayers: boolean }): ReactNode {
  const { t } = useTranslation('console');
  const records = sale.payments.map((payment: SalePayment) => ({
    tender: payment.tender,
    amount: payment.amount,
    tendered: payment.tendered,
    change: payment.change,
    changeToWallet: payment.changeToWallet === undefined ? null : { amount: payment.changeToWallet.amount },
  }));
  // The one player whose wallet took change, if any: named from the profile when the member may read players.
  const walletPlayer = sale.payments.find((payment) => payment.changeToWallet !== undefined)?.changeToWallet?.playerId;
  const rows = receiptTenderRows(records, sale.total);
  return (
    <ul className="console-session__plan">
      {rows.map((row: ReceiptTenderRow, index) => (
        <li key={index} className={`console-session__plan-row${row.kind !== 'tender' && row.nested ? ' console-order__tender-sub' : ''}`}>
          <span>
            {row.kind === 'tender' ? t(`orders.tender.${row.tender}`, { defaultValue: row.tender }) : null}
            {row.kind === 'cash_received' ? t('orders.payments.cashReceived') : null}
            {row.kind === 'change' ? t('orders.payments.change') : null}
            {row.kind === 'change_to_wallet' ? <ChangeToWalletLabel organizationId={organizationId} playerId={walletPlayer} canReadPlayers={canReadPlayers} /> : null}
          </span>
          <span className="console-money">{formatMoney(row.amount)}</span>
        </li>
      ))}
    </ul>
  );
}

/** "Change added to Asha's wallet", named from the profile when it can be read; "the player's wallet" otherwise. */
function ChangeToWalletLabel({ organizationId, playerId, canReadPlayers }: { readonly organizationId: string; readonly playerId: string | undefined; readonly canReadPlayers: boolean }): ReactNode {
  const { t } = useTranslation('console');
  const profile = usePlayerProfile(organizationId, canReadPlayers ? playerId : undefined);
  return profile.data === undefined ? t('orders.payments.changeToWalletUnnamed') : t('orders.payments.changeToWallet', { player: playerName(profile.data) });
}

/** Where a refund goes (spec/06 §8.7): back through a counter tender, or into the player's wallet as cash (2026-10-02). */
type RefundDestination = 'tender' | 'wallet' | 'online';

/** What can still go back to the player's online payment: paid online, less online refunds not declined. */
function onlineRefundable(sale: Sale): number {
  const paid = sale.payments.filter((payment) => payment.status === 'succeeded' && payment.tender === 'online').reduce((sum, payment) => sum + payment.amount.amount, 0);
  const refunded = sale.refunds.filter((one) => one.tender === 'online' && one.status !== 'declined').reduce((sum, one) => sum + one.amount.amount, 0);
  return Math.max(0, paid - refunded);
}

/** True when the sale was paid online and in no other way, so an online refund is the natural way back. */
function paidOnlyOnline(sale: Sale): boolean {
  const paid = sale.payments.filter((payment) => payment.status === 'succeeded');
  return paid.length > 0 && paid.every((payment) => payment.tender === 'online');
}

/** The counter tender the sale was paid by, as the default to refund to; cash when it was paid otherwise (wallet, online). */
function originalTenderOf(sale: Sale): Tender {
  const paidBy = sale.payments.find((payment) => payment.status === 'succeeded' && (TENDERS as readonly string[]).includes(payment.tender));
  return (paidBy?.tender as Tender | undefined) ?? 'cash';
}

function RefundForm({
  organizationId,
  venueId,
  sale,
  canPickPlayer,
  onDone,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly sale: Sale;
  readonly canPickPlayer: boolean;
  readonly onDone: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const problem = useProblem();
  const refund = useRefundSale(organizationId, venueId, sale.id);
  const currency = sale.total.currency;
  // The default keeps today's behaviour: the money goes back the way it came. The wallet is an extra choice.
  // Paid only online, the money goes back online by default; the online choice is offered whenever any was paid online.
  const hasOnline = sale.payments.some((payment) => payment.status === 'succeeded' && payment.tender === 'online');
  const online = onlineRefundable(sale);
  const [destination, setDestination] = useState<RefundDestination>(() => (paidOnlyOnline(sale) ? 'online' : 'tender'));
  const [tender, setTender] = useState<Tender>(() => originalTenderOf(sale));
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  // A sale with no player: staff attach the one whose wallet gets the refund.
  const [attached, setAttached] = useState<OrganizationPlayer | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);

  const toWallet = destination === 'wallet';
  const playerId = sale.playerId ?? attached?.id;
  const balance = useWalletBalance(organizationId, toWallet ? playerId : undefined);
  const toOnline = destination === 'online';
  const minor = toMinor(amount, currency);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (minor === null || minor <= 0) {
      setError(t('orders.refund.badAmount'));
      return;
    }
    if (toOnline && minor > online) {
      setError(t('orders.refund.onlineOver', { amount: formatMoney({ amount: online, currency }) }));
      return;
    }
    if (reason.trim() === '') {
      setError(t('orders.refund.reasonRequired'));
      return;
    }
    if (toWallet && playerId === undefined) {
      setError(t('orders.refund.wallet.playerRequired'));
      return;
    }
    setError(undefined);
    refund.mutate(
      {
        tender: toWallet ? 'wallet' : toOnline ? 'online' : tender,
        reason: reason.trim(),
        amount: { amount: minor, currency },
        ...(toWallet && sale.playerId === null && attached !== null ? { playerId: attached.id } : {}),
      },
      {
        onSuccess: () => {
          show({ tone: 'ok', title: t('orders.refund.done') });
          onDone();
        },
        onError: (mutationError) => {
          const code = problemCode(mutationError);
          const detail = toOnline ? problemDetail(mutationError) : undefined;
          setError(
            detail !== undefined && (code === PROBLEM.conflict || code === 'online_payments_unavailable')
              ? detail
              : code === PROBLEM.conflict
                ? t('orders.refund.conflict')
                : problem.text(mutationError, { fallback: t('orders.refund.refused') }),
          );
        },
      },
    );
  }

  return (
    <form className="console-session__form" aria-label={t('orders.actions.refund')} noValidate onSubmit={submit}>
      <p className="console-muted">{t('orders.refund.hint')}</p>
      <RadioGroup
        label={t('orders.refund.destination')}
        orientation="horizontal"
        options={[
          { value: 'tender', label: t('orders.refund.toTender') },
          ...(hasOnline ? [{ value: 'online', label: t('orders.refund.toOnline') }] : []),
          { value: 'wallet', label: t('orders.refund.toWallet') },
        ]}
        value={destination}
        onValueChange={(value) => {
          setDestination(value as RefundDestination);
          setError(undefined);
        }}
      />
      {toWallet ? (
        <WalletDestination
          organizationId={organizationId}
          sale={sale}
          attached={attached}
          onAttach={setAttached}
          canPickPlayer={canPickPlayer}
          balance={balance.data?.total}
          refundMinor={minor !== null && minor > 0 ? minor : null}
        />
      ) : toOnline ? (
        <div className="console-order__wallet-refund">
          <p className="console-order__wallet-preview" aria-live="polite">
            {t('orders.refund.onlineCap', { amount: formatMoney({ amount: online, currency }) })}
          </p>
          <p className="console-subtle">{t('orders.refund.onlineHelp')}</p>
        </div>
      ) : (
        <SelectField label={t('orders.payment.tender')} options={TENDERS.map((value) => ({ value, label: t(`orders.tender.${value}`) }))} value={tender} onValueChange={(value) => setTender(value as Tender)} />
      )}
      <TextField label={t('orders.refund.amount')} required inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
      <TextField label={t('orders.refund.reason')} required value={reason} onChange={(e) => setReason(e.target.value)} error={error} />
      <FormActions busy={refund.isPending} submitLabel={toWallet ? t('orders.refund.wallet.submit') : t('orders.refund.submit')} onCancel={onDone} />
    </form>
  );
}

/**
 * The wallet side of a refund (2026-10-02): whose wallet — the sale's player, or one staff attach — and the balance now →
 * after, in rupees. The refund is credited as wallet cash the player can spend or have refunded later; nothing leaves the
 * drawer. The server holds it to the wallet's maximum balance and says so if it would go over.
 */
function WalletDestination({
  organizationId,
  sale,
  attached,
  onAttach,
  canPickPlayer,
  balance,
  refundMinor,
}: {
  readonly organizationId: string;
  readonly sale: Sale;
  readonly attached: OrganizationPlayer | null;
  readonly onAttach: (player: OrganizationPlayer | null) => void;
  readonly canPickPlayer: boolean;
  readonly balance: { readonly amount: number; readonly currency: string } | undefined;
  readonly refundMinor: number | null;
}): ReactNode {
  const { t } = useTranslation('console');
  const [q, setQ] = useState('');
  const needsPlayer = sale.playerId === null && attached === null;
  const search = usePlayerSearch(organizationId, q, { enabled: needsPlayer && q.trim().length >= PLAYER_SEARCH_MIN });

  return (
    <div className="console-order__wallet-refund">
      {needsPlayer ? (
        canPickPlayer ? (
          <div className="console-player-pick">
            <SearchField label={t('orders.refund.wallet.player')} placeholder={t('orders.refund.wallet.playerHint')} value={q} onChange={(event) => setQ(event.target.value)} />
            {search.isFetching ? <Skeleton shape="text" lines={2} /> : null}
            <ul className="console-player-pick__results">
              {pickerResults(search, q).map((candidate) => (
                <li key={candidate.id}>
                  <button type="button" className="console-player-pick__result" onClick={() => onAttach(candidate)}>
                    <Avatar name={playerName(candidate)} size="sm" decorative />
                    <span className="console-player-pick__name">{playerName(candidate)}</span>
                    {candidate.phoneNumber === null ? null : <span className="console-subtle">{candidate.phoneNumber}</span>}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <Notice tone="info">{t('orders.refund.wallet.noPlayer')}</Notice>
        )
      ) : null}
      {attached !== null ? (
        <div className="console-player-chosen">
          <Avatar name={playerName(attached)} size="md" decorative />
          <span className="console-player-chosen__name">{playerName(attached)}</span>
          <Button type="button" variant="ghost" size="sm" onClick={() => onAttach(null)}>
            {t('orders.refund.wallet.playerChange')}
          </Button>
        </div>
      ) : null}
      {balance === undefined ? null : (
        <p className="console-order__wallet-preview" aria-live="polite">
          <span className="console-eyebrow">{t('orders.refund.wallet.balance')}</span>{' '}
          <span className="console-money">{formatMoney(balance)}</span>
          {refundMinor === null ? null : (
            <>
              {' → '}
              <span className="console-money">{formatMoney({ amount: balance.amount + refundMinor, currency: balance.currency })}</span>
            </>
          )}
        </p>
      )}
      <p className="console-subtle">{t('orders.refund.wallet.note')}</p>
    </div>
  );
}

/** The receipt's last email and how it went (F-POS-13): on its way, sent (to whom, when), or why not. */
export function DeliveryLine({ delivery }: { readonly delivery: NonNullable<SaleDocumentDelivery> }): ReactNode {
  const { t } = useTranslation('console');
  const time = delivery.completedAt === null ? '' : new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(delivery.completedAt));
  const to = delivery.to ?? '';
  if (delivery.status === 'queued') {
    return (
      <p className="console-muted" role="status" data-delivery="queued">
        {to === '' ? t('orders.document.delivery.queuedPlayer') : t('orders.document.delivery.queued', { to })}
      </p>
    );
  }
  if (delivery.status === 'sent') {
    return (
      <p className="console-muted" role="status" data-delivery="sent">
        {t(delivery.auto ? 'orders.document.delivery.sentAuto' : 'orders.document.delivery.sent', { to, time })}
      </p>
    );
  }
  const reason = t(`orders.document.delivery.reason.${delivery.failure ?? 'other'}`, { defaultValue: t('orders.document.delivery.reason.other') });
  return (
    <Notice tone="danger" role="alert">
      {to === '' ? t('orders.document.delivery.failedNoAddress', { reason }) : t('orders.document.delivery.failed', { to, reason })}
    </Notice>
  );
}

function SendForm({ organizationId, venueId, saleId, onDone }: { readonly organizationId: string; readonly venueId: string; readonly saleId: string; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const send = useSendSaleDocument(organizationId, venueId, saleId);
  const [to, setTo] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(undefined);
    send.mutate(
      { channel: 'email', ...(to.trim() === '' ? {} : { to: to.trim() }) },
      {
        onSuccess: onDone,
        onError: (mutationError) => {
          const code = problemCode(mutationError);
          setError(
            code === PROBLEM.conflict
              ? t('orders.send.noContact')
              : code === PROBLEM.validationFailed
                ? (problem.fieldMessage(mutationError, 'to') ?? t('orders.send.badAddress'))
                : problem.text(mutationError, { fallback: t('orders.send.refused') }),
          );
        },
      },
    );
  }

  return (
    <form className="console-session__form" aria-label={t('orders.actions.resend')} noValidate onSubmit={submit}>
      <p className="console-muted">{t('orders.send.hint')}</p>
      <TextField label={t('orders.send.to')} type="email" inputMode="email" autoComplete="off" showOptional value={to} onChange={(e) => setTo(e.target.value)} error={error} />
      <FormActions busy={send.isPending} submitLabel={t('orders.send.submit')} onCancel={onDone} />
    </form>
  );
}

function CancelForm({ organizationId, venueId, saleId, onDone }: { readonly organizationId: string; readonly venueId: string; readonly saleId: string; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const cancel = useCancelSale(organizationId, venueId, saleId);
  const [error, setError] = useState<string | undefined>(undefined);

  return (
    <div className="console-session__form" role="group" aria-label={t('orders.actions.cancel')}>
      <p className="console-session__form-title">{t('orders.cancel.title')}</p>
      <p className="console-muted">{t('orders.cancel.body')}</p>
      {error !== undefined ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}
      <div className="console-form__actions">
        <Button variant="danger" className="clav-button--danger-solid" size="sm" busy={cancel.isPending} onClick={() => cancel.mutate(undefined, { onSuccess: onDone, onError: (failure) => setError(problem.text(failure, { fallback: t('orders.cancel.refused') })) })}>
          {t('orders.cancel.confirm')}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>
          {t('orders.cancel.keep')}
        </Button>
      </div>
    </div>
  );
}

function FormActions({ busy, submitLabel, onCancel }: { readonly busy: boolean; readonly submitLabel: string; readonly onCancel: () => void }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <div className="console-form__actions">
      <Button type="submit" variant="primary" size="sm" busy={busy}>
        {submitLabel}
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
        {t('orders.actions.dismiss')}
      </Button>
    </div>
  );
}
